import React, { useEffect } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { Box, CircularProgress, Typography } from '@mui/material';

// Auth components
import Login from './pages/auth/Login';
import ProtectedRoute from './components/auth/ProtectedRoute';

// Layout components
import MainLayout from './components/layouts/MainLayout';

// Page components
import Dashboard from './pages/Dashboard';
import Presentations from './pages/presentations/Presentations';
import PresentationDetail from './pages/presentations/PresentationDetail';
import CreatePresentation from './pages/presentations/CreatePresentation';
import EditPresentation from './pages/presentations/EditPresentation';
import ActiveVoting from './pages/voting/ActiveVoting';
import VoteResults from './pages/voting/VoteResults';
import UserManagement from './pages/admin/UserManagement';
import NotFound from './pages/NotFound';

// Redux
import { useAppDispatch, useAppSelector } from './hooks/redux';
import { getCurrentUser } from './services/slices/authSlice';

const App: React.FC = () => {
  const dispatch = useAppDispatch();
  const { isAuthenticated, loading } = useAppSelector((state) => state.auth);

  useEffect(() => {
    // Check if user is already authenticated
    dispatch(getCurrentUser());
  }, [dispatch]);

  if (loading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="100vh"
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <>
      {/* Top Banner */}
      <Box
        sx={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          zIndex: 9999,
          backgroundColor: 'rgba(0, 0, 0, 0.9)',
          backdropFilter: 'blur(10px)',
          borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
          py: 0.5,
        }}
      >
        <Typography
          variant="caption"
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 0.5,
            color: 'rgba(255, 255, 255, 0.8)',
          }}
        >
          Built with{' '}
          <Box
            component="span"
            sx={{
              color: '#ff4757',
              fontSize: '1em',
              animation: 'heartbeat 1.5s ease-in-out infinite',
              '@keyframes heartbeat': {
                '0%': { transform: 'scale(1)' },
                '50%': { transform: 'scale(1.1)' },
                '100%': { transform: 'scale(1)' },
              },
            }}
          >
            ❤️
          </Box>{' '}
          by{' '}
          <Box
            component="a"
            href="https://www.openana.ai"
            target="_blank"
            rel="noopener noreferrer"
            sx={{
              color: '#3742fa',
              textDecoration: 'none',
              fontWeight: 'bold',
              transition: 'all 0.2s ease',
              '&:hover': {
                color: '#5352ed',
                textDecoration: 'underline',
                transform: 'scale(1.05)',
              },
            }}
          >
            Ana
          </Box>
        </Typography>
      </Box>

      {/* Main App Content */}
      <Box sx={{ pt: '40px' }}>
        <Routes>
          {/* Public routes */}
          <Route path="/login" element={!isAuthenticated ? <Login /> : <Navigate to="/" />} />

          {/* Protected routes */}
          <Route element={<ProtectedRoute isAuthenticated={isAuthenticated} />}>
            <Route element={<MainLayout />}>
              {/* Dashboard */}
              <Route path="/" element={<Dashboard />} />

              {/* Presentations */}
              <Route path="/presentations" element={<Presentations />} />
              <Route path="/presentations/new" element={<CreatePresentation />} />
              <Route path="/presentations/:id" element={<PresentationDetail />} />
              <Route path="/presentations/:id/edit" element={<EditPresentation />} />

              {/* Voting */}
              <Route path="/vote/:presentationId" element={<ActiveVoting />} />
              <Route path="/results/:presentationId" element={<VoteResults />} />

              {/* Admin */}
              <Route path="/admin/users" element={<UserManagement />} />
            </Route>
          </Route>

          {/* 404 Not Found */}
          <Route path="*" element={<NotFound />} />
        </Routes>
      </Box>
    </>
  );
};

export default App;
